import { Button } from '@kizen/kds/Button';
import { Spacer } from '@kizen/kds/Spacer';
import { Typography } from '@kizen/kds/Typography';
import { useBreakpoint } from '__app/spacing';
import useModal from '__components/Modals/useModal';
import { usePluginKZNImporter } from '__pages/Marketplace/components/Importer/usePluginKZNImporter';
import { ImportProgressStep } from '__pages/Marketplace/components/Importer/ImportProgressStep';
import { getFilePath } from '__pages/Marketplace/components/Tile';
import { ConfirmationModal } from '__pages/Marketplace/components/Tile/ConfirmationModal';
import { WarningModal } from '__pages/Marketplace/components/Tile/WarningModal';
import { useMarketplacePermissions } from '__pages/Marketplace/permissions';
import { Thumbnail, ThumbnailSizer } from '__pages/Marketplace/styles';
import { MarketplacePlugin } from '__pages/Marketplace/types';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { InstalledPlugin } from 'ts-components/Plugins/types';

type DetailsPanelProps = {
  plugin: MarketplacePlugin;
  installedPlugin?: InstalledPlugin;
  pendingChange?: {
    apiName: string;
    isInstalling: boolean;
  };
  onChangeEnabled: (
    apiName: string,
    enabled: boolean,
    plugin: MarketplacePlugin
  ) => void;
  businessPluginAppsByApiName: Record<string, any>;
  onImportKZNFileStep: (pluginAPIName: string, etag?: string) => Promise<void>;
  importEvents: Array<any>;
};

export const DetailsPanel = ({
  plugin,
  installedPlugin,
  pendingChange,
  onChangeEnabled,
  businessPluginAppsByApiName,
  onImportKZNFileStep,
  importEvents,
}: DetailsPanelProps) => {
  const { t } = useTranslation();
  const history = useHistory();

  const isMobile = useBreakpoint();

  const [confirmationModalProps, , confirmationModal] = useModal({
    handleSubmit: async () => {
      onChangeEnabled(plugin.api_name, false, plugin);
    },
  });

  const [overrideModalProps, , overrideModal] = useModal({
    handleSubmit: async () => {
      await onChangeEnabled?.(plugin.api_name, true, plugin);
      history.push(`/marketplace/${plugin.api_name}/configure`);
    },
  });

  const handleClickEnable = useCallback(() => {
    if (plugin.api_name === 'intercom') {
      return overrideModal.show();
    }

    return onChangeEnabled(plugin.api_name, true, plugin);
  }, [onChangeEnabled, plugin, overrideModal]);

  const { isReadOnly } = useMarketplacePermissions(plugin);

  const {
    hasKZNFile,
    hasImportedKZNFile,
    schemaImportIsLatest,
    schemaImportTs,
    onImport,
    pending: schemaImportPending,
    importProgress,
  } = usePluginKZNImporter({
    config: plugin,
    businessPluginAppsByApiName,
    onImportKZNFileStep,
  });

  const importErrorEvents = useMemo(() => {
    return importEvents.filter((event) => event.event_type === 'error');
  }, [importEvents]);

  return (
    <div className={`flex gap-20 ${isMobile ? 'flex-col' : 'flex-row'}`}>
      {plugin.thumbnail ? (
        <div>
          <ThumbnailSizer size={200}>
            <Thumbnail src={getFilePath(plugin.thumbnail)} fullWidth />
          </ThumbnailSizer>
        </div>
      ) : null}
      <div>
        <Typography size="2xl" weight="semibold" variant="header">
          {plugin.name}
        </Typography>
        <Spacer size={15} />
        <Typography>
          {installedPlugin
            ? t('Installed version: {{version}}', {
                version: installedPlugin.version,
              })
            : t('Not currently installed')}
        </Typography>
        {isReadOnly ? null : !installedPlugin ? (
          <>
            <Spacer size={15} />
            <Button
              color="primary"
              onClick={handleClickEnable}
              disabled={
                pendingChange?.apiName === plugin.api_name ? true : false
              }
            >
              {t('Install App')}
            </Button>
          </>
        ) : (
          <>
            <Spacer size={15} />
            <Button
              color="warning"
              onClick={confirmationModal.show}
              disabled={
                pendingChange?.apiName === plugin.api_name ? true : false
              }
              outline
            >
              {t('Uninstall App')}
            </Button>
          </>
        )}
        {plugin.external_link ? (
          <>
            <Spacer size={15} />
            <Typography variant="link" href={plugin.external_link}>
              {plugin.external_link}
            </Typography>
          </>
        ) : null}
        {plugin.github_repo && plugin.is_open_source ? (
          <>
            <Spacer size={15} />
            <Typography
              variant="link"
              href={`https://github.com/${plugin.github_repo}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('View Source')}
            </Typography>
          </>
        ) : null}
        {plugin.description ? (
          <>
            <Spacer size={30} />
            <Typography variant="header" weight="semibold">
              {t('About This App')}
            </Typography>
            <Spacer size={15} />

            <Typography preserveNewlines>{plugin.description}</Typography>
          </>
        ) : null}
        {plugin.managed_by_business?.reply_to_email ? (
          <>
            <Spacer size={30} />
            <Typography weight="semibold" variant="header">
              {t('App Support')}
            </Typography>
            <Spacer size={15} />
            <div className="flex">
              <Typography
                variant="link"
                href={`mailto:${plugin.managed_by_business.reply_to_email}`}
              >
                {plugin.managed_by_business.reply_to_email}
              </Typography>
            </div>
          </>
        ) : null}
        {hasKZNFile ? (
          <>
            <Spacer size={30} />
            <Typography weight="semibold" variant="header">
              {t('App Schema')}
            </Typography>
            <Spacer size={15} />
            {importErrorEvents.map((msg) => (
              <>
                <div key={msg.context.created} className="flex">
                  <div className="bg-viz-seq-red-10 flex p-[10px] rounded-[4px]">
                    <Typography>
                      {t(
                        'The most recent app schema import failed. Check your email for further details.'
                      )}
                    </Typography>
                  </div>
                </div>
                <Spacer size={10} />
              </>
            ))}
            {!hasImportedKZNFile ? (
              <Typography>{t('App schema has not been imported')}</Typography>
            ) : (
              <Typography>
                {t('App schema last imported on {{date}} at {{time}}', {
                  date: new Date(schemaImportTs).toLocaleDateString(),
                  time: new Date(schemaImportTs).toLocaleTimeString(),
                })}
              </Typography>
            )}
            {importProgress ? (
              <>
                <Spacer size={10} />
                <ImportProgressStep importProgress={importProgress} />
              </>
            ) : null}
            {schemaImportIsLatest ||
            isReadOnly ||
            importProgress?.phase === 'polling' ? null : (
              <>
                <Spacer size={10} />
                <Typography>
                  {!hasImportedKZNFile
                    ? t(
                        'If you proceed, your business will be updated with the new schema. If the app is later uninstalled, these changes will remain.'
                      )
                    : t(
                        'A new version of the app schema is available. You can re-import it to update your business setup.'
                      )}
                </Typography>
                {importErrorEvents.length === 0 ? (
                  <>
                    <Spacer size={10} />

                    <Button
                      onClick={onImport}
                      disabled={schemaImportPending}
                      variant="text"
                      size="xs"
                      color="secondary"
                    >
                      {!hasImportedKZNFile
                        ? t('Import Schema')
                        : t('Update Schema')}
                    </Button>
                  </>
                ) : null}
              </>
            )}
            {importErrorEvents.length > 0 &&
            importProgress?.phase !== 'polling' ? (
              <>
                <Spacer size={10} />
                <Button onClick={onImport} disabled={schemaImportPending}>
                  {t('Re-Import Schema')}
                </Button>
              </>
            ) : null}
          </>
        ) : null}
      </div>
      <ConfirmationModal
        confirmationModalProps={confirmationModalProps}
        installedPlugin={plugin}
      />
      <WarningModal {...overrideModalProps} plugin={plugin} />
    </div>
  );
};
